// ABOUTME: Penalty notification store and dialog component
// ABOUTME: Manages global state for displaying penalty notifications to users

import { AlertTriangle, Ban, Clock, Info } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalTitle,
} from "@/shared/ui/responsive-modal";

type PenaltyInfo = {
  reason: string;
  current: number;
  limit: number;
  untilBan: number;
  autoBansCount: number;
  totalPenalties: number;
};

type PenaltyNotificationInternalState = {
  isOpen: boolean;
  penaltyInfo: PenaltyInfo | null;
};

const penaltyNotificationStore = createPenaltyNotificationStore();

function createPenaltyNotificationStore() {
  let state: PenaltyNotificationInternalState = {
    isOpen: false,
    penaltyInfo: null,
  };

  const listeners = new Set<() => void>();

  function notifyListeners() {
    listeners.forEach((listener) => listener());
  }

  return {
    getState: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    showPenalty: (info: PenaltyInfo) => {
      state = { isOpen: true, penaltyInfo: info };
      notifyListeners();
    },
    hidePenalty: () => {
      state = { isOpen: false, penaltyInfo: null };
      notifyListeners();
    },
  };
}

function usePenaltyNotificationState() {
  const [state, setState] = React.useState(() =>
    penaltyNotificationStore.getState(),
  );

  React.useEffect(() => {
    const unsubscribe = penaltyNotificationStore.subscribe(() => {
      setState(penaltyNotificationStore.getState());
    });
    return unsubscribe;
  }, []);

  return state;
}

export function showPenalty(info: PenaltyInfo) {
  penaltyNotificationStore.showPenalty(info);
}

export function hidePenalty() {
  penaltyNotificationStore.hidePenalty();
}

function BanTier({
  tier,
  duration,
  isActive,
}: {
  tier: number;
  duration: string;
  isActive: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${isActive ? "bg-amber-500/20 font-medium" : "bg-muted/30"}`}
    >
      <div
        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${isActive ? "bg-amber-500 text-white" : "bg-muted-foreground/20 text-muted-foreground"}`}
      >
        {tier}
      </div>
      <span className={isActive ? "text-amber-700" : "text-muted-foreground"}>
        {t("penalty.ban_tier", { duration })}
      </span>
    </div>
  );
}

function PenaltyProgress({
  current,
  limit,
}: {
  current: number;
  limit: number;
}) {
  const { t } = useTranslation();
  const percentage = (current / limit) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-muted-foreground">
          {t("penalty.progress_title")}
        </span>
        <span className="font-semibold text-amber-600">
          {current} / {limit}
        </span>
      </div>
      <div className="h-3 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5" />
        <span>{t("penalty.until_ban", { count: limit - current, limit })}</span>
      </div>
    </div>
  );
}

export function PenaltyNotificationDialog() {
  const { t } = useTranslation();
  const { isOpen, penaltyInfo } = usePenaltyNotificationState();

  if (!penaltyInfo) return null;

  const banDurations = [
    t("penalty.durations.2h"),
    t("penalty.durations.5h"),
    t("penalty.durations.12h"),
    t("penalty.durations.permanent"),
  ];

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) hidePenalty();
      }}
    >
      <ResponsiveModalContent showCloseButton className="max-w-md">
        <ResponsiveModalHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
          </div>
          <ResponsiveModalTitle className="text-xl">
            {t("penalty.title")}
          </ResponsiveModalTitle>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-5">
          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm font-medium text-muted-foreground">
              {t("penalty.reason_label")}
            </p>
            <p className="mt-1 text-sm">{penaltyInfo.reason}</p>
          </div>

          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
            <PenaltyProgress
              current={penaltyInfo.current}
              limit={penaltyInfo.limit}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Ban className="h-4 w-4" />
              <span>{t("penalty.ban_escalation")}</span>
            </div>
            <div className="space-y-1.5">
              {banDurations.map((duration, index) => (
                <BanTier
                  key={index}
                  tier={index + 1}
                  duration={duration}
                  isActive={index === penaltyInfo.autoBansCount}
                />
              ))}
            </div>
            {penaltyInfo.autoBansCount > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {t("penalty.previous_bans", {
                    count: penaltyInfo.autoBansCount,
                  })}
                </span>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={hidePenalty}
            className="w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-amber-600"
          >
            {t("penalty.understand")}
          </button>
        </ResponsiveModalBody>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
